import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Link,
  List,
  ListItem,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useToken } from "@rentablo/api";
import { useMandatorContext } from "@rentablo/theming";
import MenuIcon from "@icons/burger.svg?react";
import CloseIcon from "@icons/close.svg?react";
import {
  BLOG,
  FAQS,
  FORUM,
  HOME,
  LOGIN,
  YOUR_DISCOUNT,
} from "@helpers/urlConstants";

const NAV_PANEL_HEIGHT = 58;

const MenuWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "-5px",
  left: "-12px",
  right: "-12px",
  width: "100vw",
  marginTop: `${NAV_PANEL_HEIGHT}px`,
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "space-between",
  borderTop: `1px solid ${theme.palette.border.primary}`,
}));

const MobileMenuContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
}));

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(4, 4),
  cursor: "pointer",
  boxShadow: `inset 0px -1px 0px ${theme.palette.border.primary}`,

  "& .MuiTypography-root": {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(3),
}));

const AuthenticationButton = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return !isLoggedIn ? (
    <LoginButton href={LOGIN} variant="outlined" size="small">
      {t("redesign.header.actions.login")}
    </LoginButton>
  ) : (
    <Button
      href={HOME}
      variant="primary"
      size="small"
      sx={{ marginRight: theme.spacing(3) }}
    >
      {t("redesign.header.actions.inApp")}
    </Button>
  );
};

export const HeaderMobileMenu = () => {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const { isSuccess: isTokenSuccess } = useToken();
  const { t } = useTranslation();
  const { isRentablo } = useMandatorContext();

  const setMenuHeight = useCallback(() => {
    setHeight(window.innerHeight - NAV_PANEL_HEIGHT);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- window.innerHeight + resize listener
    setMenuHeight();
    window.addEventListener("resize", setMenuHeight);

    return () => {
      window.removeEventListener("resize", setMenuHeight);
    };
  }, [setMenuHeight]);

  // prevent scrolling when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    document.body.style.height = open ? "100%" : "auto";
  }, [open]);

  return (
    <MobileMenuContainer>
      <AuthenticationButton isLoggedIn={isTokenSuccess} />
      {isRentablo && (
        <IconButton
          size="small"
          onClick={() => {
            setOpen((item) => !item);
          }}
          sx={{ padding: 0 }}
        >
          {!open && <MenuIcon width="42" height="42" />}
          {open && <CloseIcon width="42" height="42" />}
        </IconButton>
      )}
      {open && (
        <MenuWrapper height={`${height}px`}>
          <List>
            <ListItemStyled>
              <Link
                href={`${import.meta.env.VITE_APP_ROOT_URL}/${YOUR_DISCOUNT}`}
              >
                <Typography color="textSecondary">
                  {t("redesign.header.actions.yourDiscount")}
                </Typography>
              </Link>
            </ListItemStyled>

            <ListItemStyled>
              <Link href={`${import.meta.env.VITE_APP_ROOT_URL}/${FAQS}`}>
                <Typography color="textSecondary">
                  {t("redesign.header.actions.faqs")}
                </Typography>
              </Link>
            </ListItemStyled>

            <ListItemStyled>
              <Link href={FORUM}>
                <Typography color="textSecondary">
                  {t("redesign.header.actions.forum")}
                </Typography>
              </Link>
            </ListItemStyled>

            <ListItemStyled>
              <Link href={BLOG}>
                <Typography color="textSecondary">
                  {t("redesign.header.actions.blog")}
                </Typography>
              </Link>
            </ListItemStyled>

            <ListItemStyled>
              <Link href={`mailto:${t("aboutCompany.support.email")}`}>
                <Typography color="textSecondary">
                  {t("redesign.header.actions.support")}
                </Typography>
              </Link>
            </ListItemStyled>
            {/*<ListItemStyled>*/}
            {/*  <Link href={ABOUT_US}>*/}
            {/*    <Typography color="textSecondary">*/}
            {/*      {t("redesign.header.actions.aboutUs")}*/}
            {/*    </Typography>*/}
            {/*  </Link>*/}
            {/*</ListItemStyled>*/}
          </List>
        </MenuWrapper>
      )}
    </MobileMenuContainer>
  );
};
